import { GENERIC_DESCRIPTION_BLOCKLIST } from './constants/index.js';
import {
  DESCRIPTION_PREFIX_PATTERNS,
  LEADING_REFERENCE,
  PAYMENT_PROCESSORS,
} from './constants/prefixes.js';

export type ParsedDescription = {
  prefix: string | null;
  date: string | null;
  merchant: string;
  fullDescription: string;
};

function cleanText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function normalizeRulePattern(pattern: string): string {
  return cleanText(pattern).toLowerCase();
}

export function isGenericRulePattern(pattern: string): boolean {
  const normalized = normalizeRulePattern(pattern);
  return GENERIC_DESCRIPTION_BLOCKLIST.some((term) => normalized === term);
}

/** Drop the bank reference numbers in front of the text (there can be two) */
function stripLeadingReferences(text: string): string {
  let rest = text;
  while (LEADING_REFERENCE.test(rest)) {
    const next = rest.replace(LEADING_REFERENCE, '');
    if (!next) break;
    rest = next;
  }
  return rest;
}

export function parseDescription(description: string): ParsedDescription {
  const fullDescription = cleanText(description);
  const text = stripLeadingReferences(fullDescription);

  for (const pattern of DESCRIPTION_PREFIX_PATTERNS) {
    const match = text.match(pattern.regex);
    if (!match) continue;

    const prefix = cleanText(match[pattern.prefixGroup] ?? '');
    const date = pattern.dateGroup ? cleanText(match[pattern.dateGroup] ?? '') : '';
    let merchant = cleanText(match[pattern.merchantGroup] ?? text);
    const message = pattern.messageGroup ? cleanText(match[pattern.messageGroup] ?? '') : '';
    // The message precedes the prefix in the text, so prefix + message would
    // never occur verbatim; drop the prefix when the message names the shop
    const usesMessage = message !== '' && PAYMENT_PROCESSORS.test(merchant);
    // A very short free-text message ("Mat") would match far too much on its
    // own; keep the whole text without the bank reference instead
    if (usesMessage) merchant = message.length >= 4 ? message : text;

    return {
      prefix: (usesMessage ? '' : prefix) || null,
      date: date || null,
      merchant: merchant || text,
      fullDescription,
    };
  }

  return {
    prefix: null,
    date: null,
    merchant: text,
    fullDescription,
  };
}

export function extractDescriptionSearchText(description: string): string {
  return normalizeRulePattern(parseDescription(description).merchant);
}

const INCOMING_SWISH = /swish\s+inbetalning/i;

/**
 * An incoming Swish (amount in öre > 0). Each one is judged by hand: a gift
 * is income, a repaid share offsets spending. So these never get a category
 * rule, a rule suggestion or an automatic category.
 */
export function isIncomingSwish(description: string, amount: number): boolean {
  return amount > 0 && INCOMING_SWISH.test(description);
}

/** A rule pattern that would target incoming Swish rows as such */
export function isIncomingSwishPattern(pattern: string): boolean {
  return INCOMING_SWISH.test(pattern);
}

/**
 * The pattern to suggest when a rule is created from a transaction: the
 * merchant without bank reference, prefix or purchase date, so the rule keeps
 * matching next month ("willys vaxjo i11", not "kortköp 260126 willys …").
 */
export function defaultRulePattern(description: string): string {
  const merchant = extractDescriptionSearchText(description);
  if (!isGenericRulePattern(merchant)) return merchant;
  // A bare "Överföring" or "Kortköp" would match far too much: fall back to
  // the full text (reference included, so it only matches this kind of row)
  return normalizeRulePattern(description);
}
